import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Save, CheckCircle, XCircle, Package, 
  Loader2, AlertTriangle, Calculator, SkipForward
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = process.env.REACT_APP_BACKEND_URL;
import { cycleCountHeaders, cycleCountPath } from './cycleCountContext';

const getHeaders = cycleCountHeaders;

const abcColors = {
  A: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  B: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  C: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
};

const statusColors = {
  SCHEDULED: 'bg-blue-100 text-blue-700',
  IN_PROGRESS: 'bg-purple-100 text-purple-700',
  COMPLETED: 'bg-green-100 text-green-700',
  CANCELLED: 'bg-gray-100 text-gray-500'
};

export default function CycleCountDetailPage() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [task, setTask] = useState(null);
  const [lines, setLines] = useState([]);
  const [countedValues, setCountedValues] = useState({});
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [applyAdjustments, setApplyAdjustments] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Load task detail
  const loadTask = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/api/inventory/cycle-count/tasks/${taskId}`, {
        headers: getHeaders()
      });
      
      if (!res.ok) throw new Error('Failed to load task');
      
      const data = await res.json();
      setTask(data);
      setLines(data.lines || []);
      
      // Initialize counted values from existing data
      const initial = {};
      (data.lines || []).forEach(line => {
        if (line.counted_qty !== null) {
          initial[line.id] = line.counted_qty.toString();
        }
      });
      setCountedValues(initial);
    } catch (err) {
      console.error('Load error:', err);
      toast.error('Failed to load task');
      navigate(cycleCountPath());
    } finally {
      setLoading(false);
    }
  }, [taskId, navigate]);

  useEffect(() => {
    loadTask();
  }, [loadTask]);

  // Start task if SCHEDULED
  const handleStart = async () => {
    try {
      const res = await fetch(`${API}/api/inventory/cycle-count/tasks/${taskId}/start`, {
        method: 'POST',
        headers: getHeaders()
      });
      
      if (!res.ok) throw new Error('Start failed');
      
      toast.success('Count started');
      loadTask();
    } catch (err) {
      toast.error('Failed to start count');
    }
  };

  // Update counted value
  const handleCountedChange = (lineId, value) => {
    setCountedValues(prev => ({
      ...prev,
      [lineId]: value
    }));
  };

  // Save all lines
  const handleSave = async () => {
    try {
      setSaving(true);
      
      const updates = Object.entries(countedValues)
        .filter(([_, value]) => value !== '' && value !== null)
        .map(([lineId, value]) => ({
          lineId,
          countedQty: parseFloat(value)
        }));
      
      if (updates.length === 0) {
        toast.info('No changes to save');
        return;
      }
      
      const res = await fetch(`${API}/api/inventory/cycle-count/tasks/${taskId}/lines`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ lines: updates })
      });
      
      if (!res.ok) throw new Error('Save failed');
      
      const data = await res.json();
      toast.success(`Saved ${data.updated} items`);
      loadTask();
    } catch (err) {
      toast.error('Failed to save counts');
    } finally {
      setSaving(false);
    }
  };

  // Skip a line
  const handleSkip = async (lineId) => {
    try {
      const res = await fetch(`${API}/api/inventory/cycle-count/tasks/${taskId}/lines`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ lines: [{ lineId, status: 'SKIPPED' }] })
      });
      
      if (!res.ok) throw new Error('Skip failed');
      
      toast.success('Item skipped');
      loadTask();
    } catch (err) {
      toast.error('Failed to skip item');
    }
  };

  // Complete task
  const handleComplete = async () => {
    try {
      setCompleting(true);
      
      // First save any pending counts
      const updates = Object.entries(countedValues)
        .filter(([_, value]) => value !== '' && value !== null)
        .map(([lineId, value]) => ({
          lineId,
          countedQty: parseFloat(value)
        }));
      
      if (updates.length > 0) {
        await fetch(`${API}/api/inventory/cycle-count/tasks/${taskId}/lines`, {
          method: 'PUT',
          headers: getHeaders(),
          body: JSON.stringify({ lines: updates })
        });
      }
      
      // Then complete
      const res = await fetch(`${API}/api/inventory/cycle-count/tasks/${taskId}/complete`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ applyAdjustments })
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Complete failed');
      }
      
      const data = await res.json();
      toast.success(`Count completed! ${applyAdjustments ? `${data.summary.adjustments_created} adjustments applied.` : 'No adjustments applied.'}`);
      navigate(cycleCountPath());
    } catch (err) {
      toast.error(err.message || 'Failed to complete count');
    } finally {
      setCompleting(false);
      setShowCompleteDialog(false);
    }
  };

  // Filter lines by search
  const filteredLines = lines.filter(line => 
    !searchQuery || 
    line.item_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    line.item_sku?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate summary
  const pendingCount = lines.filter(l => l.status === 'PENDING').length;
  const countedCount = lines.filter(l => l.status === 'COUNTED').length;
  const skippedCount = lines.filter(l => l.status === 'SKIPPED').length;
  const totalVariance = lines.reduce((sum, l) => sum + (l.variance_value_cents || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <img src="/chain29-logo.png" alt="Loading" className="w-12 h-12 rounded-xl object-cover animate-pulse" />
      </div>
    );
  }

  if (!task) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Task not found</p>
        <Button variant="link" onClick={() => navigate(cycleCountPath())}>
          Back to list
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cycle-count-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(cycleCountPath())}
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold dark:text-[#d6deeb]">{task.task_number}</h1>
              <Badge className={abcColors[task.abc_class]}>{task.abc_class} Items</Badge>
              <Badge className={statusColors[task.status]}>{task.status.replace('_', ' ')}</Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              Due: {task.due_date} {task.is_overdue && <span className="text-red-500">(Overdue)</span>}
            </p>
          </div>
        </div>
        
        <div className="flex gap-2">
          {task.status === 'SCHEDULED' && (
            <Button onClick={handleStart} data-testid="start-count-btn">
              <CheckCircle className="h-4 w-4 mr-2" />
              Start Count
            </Button>
          )}
          {task.status === 'IN_PROGRESS' && (
            <>
              <Button variant="outline" onClick={handleSave} disabled={saving} data-testid="save-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save
              </Button>
              <Button 
                onClick={() => setShowCompleteDialog(true)} 
                disabled={pendingCount > 0}
                data-testid="complete-btn"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Complete
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="dark:bg-[#0b2942] dark:border-[#1d3b53]">
          <CardContent className="pt-4">
            <div className="text-sm text-muted-foreground">Pending</div>
            <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{pendingCount}</div>
          </CardContent>
        </Card>
        <Card className="dark:bg-[#0b2942] dark:border-[#1d3b53]">
          <CardContent className="pt-4">
            <div className="text-sm text-muted-foreground">Counted</div>
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">{countedCount}</div>
          </CardContent>
        </Card>
        <Card className="dark:bg-[#0b2942] dark:border-[#1d3b53]">
          <CardContent className="pt-4">
            <div className="text-sm text-muted-foreground">Skipped</div>
            <div className="text-2xl font-bold text-gray-500">{skippedCount}</div>
          </CardContent>
        </Card>
        <Card className="dark:bg-[#0b2942] dark:border-[#1d3b53]">
          <CardContent className="pt-4">
            <div className="text-sm text-muted-foreground">Variance Value</div>
            <div className={`text-2xl font-bold ${totalVariance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              ${(totalVariance / 100).toFixed(2)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Items List */}
      <Card className="dark:bg-[#0b2942] dark:border-[#1d3b53]">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="dark:text-[#d6deeb]">Count Items</CardTitle>
            <Input
              placeholder="Search items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-64 dark:bg-[#1d3b53] dark:border-[#2d4a63]"
              data-testid="search-items"
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {/* Header */}
            <div className="grid grid-cols-12 gap-2 p-2 text-sm font-medium text-muted-foreground border-b dark:border-[#1d3b53]">
              <div className="col-span-4">Item</div>
              <div className="col-span-2 text-right">Expected</div>
              <div className="col-span-2 text-center">Counted</div>
              <div className="col-span-2 text-right">Variance</div>
              <div className="col-span-2 text-center">Actions</div>
            </div>
            
            {/* Rows */}
            {filteredLines.map(line => {
              const countedValue = countedValues[line.id] ?? (line.counted_qty !== null ? line.counted_qty.toString() : '');
              const variance = countedValue !== '' ? parseFloat(countedValue) - line.expected_qty : null;
              const varianceValue = variance !== null && line.expected_cost_cents 
                ? (variance * line.expected_cost_cents / 100)
                : null;
              
              return (
                <div 
                  key={line.id} 
                  className={`grid grid-cols-12 gap-2 p-2 items-center rounded-lg ${
                    line.status === 'SKIPPED' ? 'opacity-50 bg-gray-50 dark:bg-gray-800/50' : 
                    line.status === 'COUNTED' ? 'bg-green-50/50 dark:bg-green-900/10' : ''
                  }`}
                  data-testid={`line-${line.id}`}
                >
                  <div className="col-span-4">
                    <div className="font-medium dark:text-[#d6deeb]">{line.item_name}</div>
                    {line.item_sku && (
                      <div className="text-xs text-muted-foreground">SKU: {line.item_sku}</div>
                    )}
                  </div>
                  <div className="col-span-2 text-right font-mono dark:text-[#d6deeb]">
                    {line.expected_qty.toFixed(2)}
                  </div>
                  <div className="col-span-2">
                    {task.status === 'IN_PROGRESS' && line.status !== 'SKIPPED' ? (
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={countedValue}
                        onChange={(e) => handleCountedChange(line.id, e.target.value)}
                        className="w-full text-center dark:bg-[#1d3b53] dark:border-[#2d4a63]"
                        placeholder="0.00"
                        data-testid={`counted-input-${line.id}`}
                      />
                    ) : (
                      <div className="text-center font-mono dark:text-[#d6deeb]">
                        {line.counted_qty !== null ? line.counted_qty.toFixed(2) : '-'}
                      </div>
                    )}
                  </div>
                  <div className="col-span-2 text-right">
                    {variance !== null && (
                      <div className={`font-mono ${variance > 0 ? 'text-green-600' : variance < 0 ? 'text-red-600' : ''}`}>
                        {variance > 0 ? '+' : ''}{variance.toFixed(2)}
                        {varianceValue !== null && (
                          <div className="text-xs text-muted-foreground">
                            ${varianceValue.toFixed(2)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="col-span-2 flex justify-center gap-1">
                    {task.status === 'IN_PROGRESS' && line.status === 'PENDING' && (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleSkip(line.id)}
                        title="Skip this item"
                      >
                        <SkipForward className="h-4 w-4" />
                      </Button>
                    )}
                    {line.status === 'COUNTED' && (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    )}
                    {line.status === 'SKIPPED' && (
                      <XCircle className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                </div>
              );
            })}
            
            {filteredLines.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No items found</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Complete Dialog */}
      <AlertDialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <AlertDialogContent className="dark:bg-[#0b2942] dark:border-[#1d3b53]">
          <AlertDialogHeader>
            <AlertDialogTitle className="dark:text-[#d6deeb]">Complete Cycle Count</AlertDialogTitle>
            <AlertDialogDescription>
              You're about to complete this cycle count with {countedCount} items counted.
              {task.items_with_variance > 0 && (
                <span className="block mt-2 text-yellow-600 dark:text-yellow-400">
                  <AlertTriangle className="h-4 w-4 inline mr-1" />
                  {task.items_with_variance} items have variance totaling ${(totalVariance / 100).toFixed(2)}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="flex items-center space-x-2 py-4">
            <Checkbox 
              id="applyAdjustments" 
              checked={applyAdjustments}
              onCheckedChange={setApplyAdjustments}
            />
            <Label htmlFor="applyAdjustments" className="dark:text-[#d6deeb]">
              Apply inventory adjustments (update stock levels)
            </Label>
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel className="dark:bg-[#1d3b53] dark:text-[#d6deeb] dark:border-[#2d4a63]">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleComplete} disabled={completing}>
              {completing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
              Complete Count
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
